import { useState, useEffect } from "react";
import { getDirectoryClient } from "sj-ui-toolkit/src/client";
import { mapIsArchived } from "sj-ui-toolkit/src/utils";
import { loadLicensesAsync } from "@/api/users";
import type { DirectoryUserShape } from "@/api/assignments";

export interface AvailableUsersMap {
  archived: boolean;
  licensed: boolean;
}

export function useAvailableUsers(currentTenantName: string | undefined): {
  availableUsers: DirectoryUserShape[];
  allUsers: Map<string, AvailableUsersMap>;
  isLoading: boolean;
} {
  const [availableUsers, setAvailableUsers] = useState<DirectoryUserShape[]>([]);
  const [allUsers, setAllUsers] = useState<Map<string, AvailableUsersMap>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!currentTenantName) return;

    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const svcDirectoryClient = getDirectoryClient();
        const [directoryRes, railsLicensesRes] = await Promise.all([
          svcDirectoryClient.getUsersForTenant(currentTenantName, { includeArchived: true }),
          loadLicensesAsync(),
        ]);

        const directoryUsers: DirectoryUserShape[] = directoryRes?.data?.users ?? [];
        const allUsersMap = new Map<string, AvailableUsersMap>();
        const licensedActiveUsers: DirectoryUserShape[] = [];
        const railsUserByUuid = new Map(
          railsLicensesRes.map(ru => [ru.directoryUuid, ru] as const)
        );

        directoryUsers.forEach(directoryUser => {
          const railsUser = railsUserByUuid.get(directoryUser.directoryUuid ?? "") ?? {};

          const isArchived = mapIsArchived(
            directoryUser as Parameters<typeof mapIsArchived>[0],
            railsUser,
            currentTenantName
          );
          const isLicensed = !!(railsUser as { licensed?: boolean }).licensed;

          if (directoryUser.email) {
            allUsersMap.set(directoryUser.email.toLowerCase(), {
              archived: isArchived,
              licensed: isLicensed,
            });
          }

          if (!isArchived && isLicensed) {
            licensedActiveUsers.push(directoryUser as DirectoryUserShape);
          }
        });

        setAvailableUsers(licensedActiveUsers);
        setAllUsers(allUsersMap);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [currentTenantName]);

  return { availableUsers, allUsers, isLoading };
}
